import 'src/global.css';

import type { GuestPageId } from './boot-data';

import { useLocation } from 'react-router';
import { lazy, useRef, Suspense, useEffect } from 'react';

import Box from '@mui/material/Box';

import { themeConfig, ThemeProvider } from 'src/theme';
import { AuthSplitLayout } from 'src/layouts/auth-split';
import { ChatFab } from 'src/layouts/dashboard/components/chat-fab';
import { I18nProvider } from 'src/locales/i18n-provider';
import { useTranslate, LocalizationProvider, useLocaleDirectionSync } from 'src/locales';

import { CONFIG } from 'src/global-config';

import { Snackbar } from 'src/components/snackbar';
import { LoadingScreen } from 'src/components/loading-screen';
import { defaultSettings, SettingsProvider } from 'src/components/settings';

import { readGuestBootData } from './boot-data';
import { GuestLanguageSwitch } from './guest-language-switch';

// ----------------------------------------------------------------------

const GUEST_PAGES: Record<GuestPageId, React.LazyExoticComponent<React.ComponentType>> = {
  signIn: lazy(() => import('src/pages/auth/guest/sign-in')),
  signUp: lazy(() => import('src/pages/auth/guest/sign-up')),
  resetPassword: lazy(() => import('src/pages/auth/guest/reset-password')),
  resetPasswordToken: lazy(() => import('src/pages/auth/guest/reset-password-token')),
  updatePassword: lazy(() => import('src/pages/auth/guest/update-password')),
};

export function GuestApp() {
  return (
    <I18nProvider>
      <SettingsProvider defaultSettings={defaultSettings}>
        <LocalizationProvider>
          <ThemeProvider
            modeStorageKey={themeConfig.modeStorageKey}
            defaultMode={themeConfig.defaultMode}
          >
            <Snackbar />
            <GuestLayout />
          </ThemeProvider>
        </LocalizationProvider>
      </SettingsProvider>
    </I18nProvider>
  );
}

function GuestLayout() {
  const { t } = useTranslate();

  useDocumentLanguage();
  useDocumentNavigation();

  const Page = GUEST_PAGES[readGuestBootData().page];

  return (
    <AuthSplitLayout
      slotProps={{
        header: { slots: { rightArea: <GuestLanguageSwitch /> } },
        section: {
          title: t('guest.panel.title'),
          subtitle: '',
          imgUrl: `${CONFIG.assetsDir}/assets/illustrations/illustration-login.svg`,
        },
      }}
    >
      <Suspense fallback={<LoadingScreen />}>
        <Page />

        <Box
          component="img"
          alt=""
          src={`${CONFIG.assetsDir}/assets/illustrations/illustration-login-mobile.svg`}
          sx={{ display: { md: 'none' }, width: 1, maxWidth: 320, mx: 'auto', mt: 4 }}
        />
      </Suspense>

      <ChatFab />
    </AuthSplitLayout>
  );
}

function useDocumentLanguage() {
  const { currentLang } = useTranslate();

  useLocaleDirectionSync();

  useEffect(() => {
    document.documentElement.lang = currentLang.value;
  }, [currentLang.value]);
}

// Each guest page is chosen and fed by its server handler, so an in-app link must load the real document.
function useDocumentNavigation() {
  const { pathname, search } = useLocation();
  const initialUrl = useRef(`${pathname}${search}`);

  useEffect(() => {
    const url = `${pathname}${search}`;
    if (url !== initialUrl.current) {
      window.location.replace(url);
    }
  }, [pathname, search]);
}
